// Gráfico de fluxo de caixa acumulado em SVG manual — projetado para
// renderização estática (PDF via html2canvas). Sem Recharts, sem animação.
// Consome a mesma série pura `buildCashFlowSeries` usada pela versão Recharts.

import type { CSSProperties } from "react";
import {
  buildCashFlowSeries,
  type CashFlowPoint,
} from "@/lib/quote-financials";

export interface QuoteCashFlowStaticChartProps {
  totalValue: number;
  monthlySavings: number;
  primaryColor?: string;
  /** Largura útil do SVG. Default 700px (cabe em A4 com paddings). */
  width?: number;
  height?: number;
  className?: string;
  style?: CSSProperties;
}

function formatShort(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}R$ ${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}R$ ${Math.round(abs / 1_000)}k`;
  return `${sign}R$ ${Math.round(abs)}`;
}

export function QuoteCashFlowStaticChart({
  totalValue,
  monthlySavings,
  primaryColor = "#10b981",
  width = 700,
  height = 260,
  className,
  style,
}: QuoteCashFlowStaticChartProps) {
  const data: CashFlowPoint[] = buildCashFlowSeries(totalValue, monthlySavings);
  if (data.length === 0) return null;

  // Margens internas do SVG
  const M = { top: 16, right: 12, bottom: 32, left: 56 };
  const innerW = width - M.left - M.right;
  const innerH = height - M.top - M.bottom;

  // Range
  const values = data.map((d) => d.valor);
  const minV = Math.min(0, ...values);
  const maxV = Math.max(0, ...values);
  const range = maxV - minV || 1;

  // Escala Y: 0 vira posição Y0
  const yOf = (v: number) =>
    M.top + innerH - ((v - minV) / range) * innerH;
  const y0 = yOf(0);

  // Largura das barras
  const slotW = innerW / data.length;
  const barW = Math.max(2, slotW - 2);

  // Ticks Y: 5 níveis equiespaçados entre minV e maxV
  const tickCount = 5;
  const ticks = Array.from({ length: tickCount + 1 }, (_, i) => {
    const v = minV + (range * i) / tickCount;
    return { v, y: yOf(v) };
  });

  // Payback
  const paybackPoint = data.find((d) => d.isPayback) ?? null;
  const paybackX = paybackPoint
    ? M.left + slotW * data.indexOf(paybackPoint) + slotW / 2
    : null;

  // Labels eixo X: a cada 5 anos + "Hoje"
  const xLabels = data.filter(
    (d) => d.yearNum === 0 || d.yearNum % 5 === 0,
  );

  return (
    <div className={className} style={style}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: "block" }}
      >
        {/* Grid horizontal + ticks Y */}
        {ticks.map((t, i) => (
          <g key={i}>
            <line
              x1={M.left}
              x2={M.left + innerW}
              y1={t.y}
              y2={t.y}
              stroke="#e5e7eb"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
            <text
              x={M.left - 6}
              y={t.y + 3}
              textAnchor="end"
              fontSize={10}
              fill="#64748b"
              fontFamily="Arial, sans-serif"
            >
              {formatShort(t.v)}
            </text>
          </g>
        ))}

        {/* Linha zero (mais forte) */}
        <line
          x1={M.left}
          x2={M.left + innerW}
          y1={y0}
          y2={y0}
          stroke="#0f172a"
          strokeWidth={1}
        />

        {/* Barras */}
        {data.map((d, i) => {
          const x = M.left + slotW * i + (slotW - barW) / 2;
          const yV = yOf(d.valor);
          const top = Math.min(y0, yV);
          const h = Math.abs(yV - y0);
          const fill = d.valor >= 0 ? "#10b981" : "#ef4444";
          return (
            <rect
              key={i}
              x={x}
              y={top}
              width={barW}
              height={Math.max(1, h)}
              fill={fill}
              rx={1.5}
            />
          );
        })}

        {/* Marcador de payback */}
        {paybackX != null && (
          <g>
            <line
              x1={paybackX}
              x2={paybackX}
              y1={M.top}
              y2={M.top + innerH}
              stroke={primaryColor}
              strokeWidth={1.5}
              strokeDasharray="4 4"
            />
            <text
              x={paybackX}
              y={M.top - 4}
              textAnchor="middle"
              fontSize={10}
              fontWeight={700}
              fill={primaryColor}
              fontFamily="Arial, sans-serif"
            >
              Payback
            </text>
          </g>
        )}

        {/* Labels eixo X */}
        {xLabels.map((d) => {
          const i = data.indexOf(d);
          const x = M.left + slotW * i + slotW / 2;
          return (
            <text
              key={d.yearNum}
              x={x}
              y={M.top + innerH + 14}
              textAnchor="middle"
              fontSize={10}
              fill="#64748b"
              fontFamily="Arial, sans-serif"
            >
              {d.year}
            </text>
          );
        })}

        {/* Título eixo X */}
        <text
          x={M.left + innerW / 2}
          y={height - 4}
          textAnchor="middle"
          fontSize={10}
          fill="#94a3b8"
          fontFamily="Arial, sans-serif"
        >
          Anos
        </text>
      </svg>
    </div>
  );
}
